import { useState } from 'react';
import { cn } from '@evogard/ui';

/**
 * A rosca dos painéis da Gestão: a fatia diz a proporção, e a lista ao lado
 * dá o número exato e a ordem, que o ângulo sozinho não dá. O total fica no
 * meio; passar o mouse numa fatia ou na linha dela troca o total pela
 * porcentagem.
 *
 * A cor é de quem chama, e é **da categoria**, não da posição na lista:
 * trocar o período reordena a lista sem trocar a cor de ninguém. Nasceu nos
 * tipos de sinistro (26/10/2026) e serve aos itens de Periféricos (28/10/2026).
 */

/* As cores de fase, numa ordem fixa: quem chama dá uma por categoria. */
export const DONUT_PALETTE = [
  'fase-azul',
  'fase-laranja',
  'fase-turquesa',
  'fase-roxo',
  'fase-amarelo',
  'fase-rosa',
  'fase-verde',
  'fase-vermelho',
  'fase-ciano',
  'fase-cinza',
] as const;
export type DonutTone = (typeof DONUT_PALETTE)[number];

const FILL: Record<DonutTone, string> = {
  'fase-azul': 'fill-fase-azul',
  'fase-laranja': 'fill-fase-laranja',
  'fase-turquesa': 'fill-fase-turquesa',
  'fase-roxo': 'fill-fase-roxo',
  'fase-amarelo': 'fill-fase-amarelo',
  'fase-rosa': 'fill-fase-rosa',
  'fase-verde': 'fill-fase-verde',
  'fase-vermelho': 'fill-fase-vermelho',
  'fase-ciano': 'fill-fase-ciano',
  'fase-cinza': 'fill-fase-cinza',
};
const STROKE: Record<DonutTone, string> = {
  'fase-azul': 'stroke-fase-azul',
  'fase-laranja': 'stroke-fase-laranja',
  'fase-turquesa': 'stroke-fase-turquesa',
  'fase-roxo': 'stroke-fase-roxo',
  'fase-amarelo': 'stroke-fase-amarelo',
  'fase-rosa': 'stroke-fase-rosa',
  'fase-verde': 'stroke-fase-verde',
  'fase-vermelho': 'stroke-fase-vermelho',
  'fase-ciano': 'stroke-fase-ciano',
  'fase-cinza': 'stroke-fase-cinza',
};
const BG: Record<DonutTone, string> = {
  'fase-azul': 'bg-fase-azul',
  'fase-laranja': 'bg-fase-laranja',
  'fase-turquesa': 'bg-fase-turquesa',
  'fase-roxo': 'bg-fase-roxo',
  'fase-amarelo': 'bg-fase-amarelo',
  'fase-rosa': 'bg-fase-rosa',
  'fase-verde': 'bg-fase-verde',
  'fase-vermelho': 'bg-fase-vermelho',
  'fase-ciano': 'bg-fase-ciano',
  'fase-cinza': 'bg-fase-cinza',
};

/** A cor do item `i` de uma lista fixa; `-1` (fora da lista) é cinza. */
export function donutToneAt(i: number): DonutTone {
  return i < 0 ? 'fase-cinza' : DONUT_PALETTE[i % DONUT_PALETTE.length]!;
}

export interface DonutSlice {
  key: string;
  label: string;
  total: number;
  tone: DonutTone;
}

const SIZE = 240;
const R = 110;
const HOLE = 68;

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/** A fatia de `a0` a `a1`, em voltas (0 é o topo, sentido horário). */
function slicePath(a0: number, a1: number): string {
  const c = SIZE / 2;
  const at = (a: number, r: number) => {
    const t = a * 2 * Math.PI - Math.PI / 2;
    return `${c + r * Math.cos(t)} ${c + r * Math.sin(t)}`;
  };
  const large = a1 - a0 > 0.5 ? 1 : 0;
  return [
    `M ${at(a0, R)}`,
    `A ${R} ${R} 0 ${large} 1 ${at(a1, R)}`,
    `L ${at(a1, HOLE)}`,
    `A ${HOLE} ${HOLE} 0 ${large} 0 ${at(a0, HOLE)}`,
    'Z',
  ].join(' ');
}

/**
 * A rosca e a lista. `noun` é o que se conta (singular e plural), para o
 * meio da rosca; `label` descreve a figura para o leitor de tela.
 */
export function DonutFigure({
  slices,
  noun,
  label,
}: {
  slices: readonly DonutSlice[];
  noun: [string, string];
  label: string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = slices.reduce((n, s) => n + s.total, 0);
  const focused = hover ? slices.find((s) => s.key === hover) : undefined;
  let at = 0;
  const arcs = slices.map((s) => {
    const a0 = at;
    at += s.total / total;
    return { ...s, a0, a1: at };
  });

  return (
    <div className="grid items-center gap-5 @md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="mx-auto block w-full max-w-[12rem]"
        role="img"
        aria-label={`${label}: ${slices.map((s) => `${s.label}, ${s.total}`).join('; ')}.`}
        onPointerLeave={() => setHover(null)}
      >
        {arcs.map((s) =>
          /* Uma categoria só é a volta inteira: o arco de 360° não se desenha. */
          s.a1 - s.a0 >= 0.9999 ? (
            <circle
              key={s.key}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={(R + HOLE) / 2}
              strokeWidth={R - HOLE}
              className={cn('fill-none', STROKE[s.tone])}
              onPointerEnter={() => setHover(s.key)}
            />
          ) : (
            <path
              key={s.key}
              d={slicePath(s.a0, s.a1)}
              strokeWidth={2}
              strokeLinejoin="round"
              className={cn(
                'stroke-superficie transition-opacity',
                FILL[s.tone],
                hover && hover !== s.key && 'opacity-35',
              )}
              onPointerEnter={() => setHover(s.key)}
            />
          ),
        )}
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 4}
          textAnchor="middle"
          className="pointer-events-none fill-texto text-[34px] font-bold tabular-nums"
        >
          {focused ? `${share(focused.total, total)}%` : total}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 26}
          textAnchor="middle"
          className="pointer-events-none fill-texto-suave text-[13px]"
        >
          {focused ? focused.label : total === 1 ? noun[0] : noun[1]}
        </text>
      </svg>
      <ol className="flex flex-col gap-2.5" aria-label={`${label}, do mais comum`}>
        {slices.map((s) => (
          <li
            key={s.key}
            className={cn(
              'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-borda text-sm transition-opacity',
              hover && hover !== s.key && 'opacity-50',
            )}
            onPointerEnter={() => setHover(s.key)}
            onPointerLeave={() => setHover(null)}
          >
            <i aria-hidden className={cn('size-3 rounded-[3px]', BG[s.tone])} />
            <span className="truncate text-texto">{s.label}</span>
            <span className="text-right text-xs text-texto-suave tabular-nums">
              <b className="font-semibold text-texto">{s.total}</b> ·{' '}
              {share(s.total, total)}%
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
